// /account/mfa/setup — 3-step TOTP enrollment wizard.
//
// Visual + copy built from
//   design/UI : UX v1.0/for-engineering-auth/specs/screen-strings.md § 4.
//
//   1. Scan    — QR + manual key + account caption + accent info callout
//   2. Verify  — 6-cell CodeInput
//   3. Save    — recovery codes + confirm-saved checkbox
//
// State machine alignment:
//   mfa-setup-required → step 1, QR not yet generated (spinner placeholder)
//   mfa-setup-pending  → step 1, QR + secret rendered (ready to scan)
//   submitting-mfa-verify → spinner overlay; same screen
//   On verify success → authenticated → AuthRoutes redirects away
//
// Enrollment is triggered eagerly on mount (when status is
// mfa-setup-required). The user doesn't need to click anything to see
// the QR — they land on the screen, the QR materializes, they scan,
// then click "I've scanned — continue →" to advance to Step 2 where
// they enter the 6-digit code Cognito has them verify.
//
// Recovery codes (step 3) are generated client-side as one-time local backup
// only. Cognito doesn't issue recovery codes natively; real backup-code flow
// is V1 work.

import { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  CodeInput,
  LangToggle,
  Spinner,
  Stepper,
} from '@gc-coach/design-system';
import { AdminAuthShell, useAuth } from '../../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import styles from './AuthScreens.module.css';

type WizardStep = 1 | 2 | 3;

export function MFASetupWizard() {
  const { t, tt, lang, setLang } = useI18n();
  const {
    status,
    error,
    enrollMfa,
    verifyMfaSetup,
    verifyMfaReenrollment,
    clearError,
  } = useAuth();

  const [step, setStep] = useState<WizardStep>(1);
  const [code, setCode] = useState('');
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  // Re-enrollment variant (post MFA recovery via Custom Auth Flow) reuses
  // the same wizard but with a different eyebrow (so the user knows they're
  // re-enrolling, not setting up for the first time) and a different verify
  // callback (verifyMfaReenrollment instead of verifyMfaSetup).
  const isRecovery =
    status.kind === 'mfa-reenrollment-pending' ||
    status.kind === 'submitting-mfa-reenrollment';

  const otpauthUri =
    status.kind === 'mfa-setup-pending' || status.kind === 'mfa-reenrollment-pending'
      ? status.otpauthUri
      : null;
  const secretCode =
    status.kind === 'mfa-setup-pending' || status.kind === 'mfa-reenrollment-pending'
      ? status.secretCode
      : null;
  const email =
    status.kind === 'mfa-setup-pending' ||
    status.kind === 'mfa-setup-required' ||
    status.kind === 'mfa-reenrollment-pending'
      ? status.email
      : '';

  // Eager enrollment: as soon as the wizard mounts in mfa-setup-required,
  // call enrollMfa() so the QR + secret materialize on Step 1 without the
  // user having to click anything first. enrollmentTriggeredRef guards
  // against React-StrictMode double-mount + against retry-on-error loops
  // — if enroll fails, the user sees the error and can navigate away
  // rather than us silently re-firing AssociateSoftwareToken.
  const enrollmentTriggeredRef = useRef(false);
  useEffect(() => {
    if (status.kind === 'mfa-setup-required' && !enrollmentTriggeredRef.current) {
      enrollmentTriggeredRef.current = true;
      void enrollMfa();
    }
  }, [status.kind, enrollMfa]);

  // Generate recovery codes once per wizard mount.
  const recoveryCodes = useMemo(() => generateRecoveryCodes(10), []);

  const submitting =
    status.kind === 'submitting-mfa-verify' ||
    status.kind === 'submitting-mfa-reenrollment' ||
    status.kind === 'submitting-login';

  const onVerify = (full: string) => {
    if (full.length !== 6 || submitting) return;
    if (isRecovery) {
      void verifyMfaReenrollment(full);
    } else {
      void verifyMfaSetup(full);
    }
  };

  // Clear the entered code when a wrong-code error fires. Covers both the
  // first-time setup flow (submitting-mfa-verify → mfa-setup-pending) and
  // the re-enrollment flow (submitting-mfa-reenrollment → mfa-reenrollment-pending).
  const prevStatusKindRef = useRef(status.kind);
  useEffect(() => {
    const wasSubmitting =
      prevStatusKindRef.current === 'submitting-mfa-verify' ||
      prevStatusKindRef.current === 'submitting-mfa-reenrollment';
    const isPendingAgain =
      status.kind === 'mfa-setup-pending' || status.kind === 'mfa-reenrollment-pending';
    if (wasSubmitting && isPendingAgain && error?.code === 'WRONG_CODE') {
      setCode('');
    }
    prevStatusKindRef.current = status.kind;
  }, [status.kind, error]);

  const stepperSteps = [
    { id: '1', label: t('mfaSetup.stepperScan') },
    { id: '2', label: t('mfaSetup.stepperVerify') },
    { id: '3', label: t('mfaSetup.stepperSave') },
  ];

  return (
    <AdminAuthShell>
      <div className={styles.topBar}>
          <LangToggle value={lang} onChange={setLang} />
        </div>

        <div className={styles.formColumn}>
          <header className={styles.header}>
            <span className={styles.eyebrow}>
              {isRecovery ? t('mfaSetup.eyebrowRecovery') : t('mfaSetup.eyebrow')}
            </span>
            <h1 className={styles.title}>
              {step === 1 && t('mfaSetup.headlineScan')}
              {step === 2 && t('mfaSetup.headlineVerify')}
              {step === 3 && t('mfaSetup.headlineSave')}
            </h1>
          </header>

          <div className={styles.wizardStepper}>
            <Stepper steps={stepperSteps} current={step - 1} />
          </div>

          <Card>
            <div className={styles.wizardStepBody}>
              {error && (
                <Alert severity="danger" onDismiss={clearError}>
                  {error.message}
                </Alert>
              )}

              {step === 1 && (
                <Step1Scan
                  otpauthUri={otpauthUri}
                  secretCode={secretCode}
                  email={email}
                  onContinue={() => setStep(2)}
                  enrollmentReady={
                    status.kind === 'mfa-setup-pending' ||
                    status.kind === 'mfa-reenrollment-pending'
                  }
                />
              )}

              {step === 2 && (
                <Step2Verify
                  code={code}
                  setCode={setCode}
                  onComplete={onVerify}
                  submitting={submitting}
                  errorMessage={error?.code === 'WRONG_CODE' ? error.message : null}
                />
              )}

              {step === 3 && (
                <Step3Save
                  recoveryCodes={recoveryCodes}
                  savedConfirmed={savedConfirmed}
                  setSavedConfirmed={setSavedConfirmed}
                />
              )}
            </div>
          </Card>

          {step === 1 && (
            <div className={styles.calloutInfo}>{t('mfaSetup.callout')}</div>
          )}

          {step === 1 && email && (
            <p className={styles.accountCaption}>
              {tt('mfaSetup.accountCaptionTemplate', { email })}
            </p>
          )}
        </div>
    </AdminAuthShell>
  );
}

// ─── Step 1: Scan ───────────────────────────────────────────────────────────

function Step1Scan({
  otpauthUri,
  secretCode,
  email: _email,
  onContinue,
  enrollmentReady,
}: {
  /** otpauth:// URI — null while enrollment is in-flight. */
  otpauthUri: string | null;
  /** Base32 TOTP secret — null while enrollment is in-flight. */
  secretCode: string | null;
  email: string;
  onContinue: () => void;
  /** True once Cognito returned the secret (state == mfa-setup-pending). */
  enrollmentReady: boolean;
}) {
  const { t } = useI18n();

  // Format secret as 4-char groups separated by spaces for readability.
  const formattedSecret = secretCode
    ? secretCode.replace(/(.{4})/g, '$1 ').trim()
    : '';

  return (
    <>
      <p className={styles.body}>{t('mfaSetup.bodyScan')}</p>

      <div className={styles.qrFrame}>
        {enrollmentReady && otpauthUri ? (
          <QRCodeSVG value={otpauthUri} size={196} level="M" />
        ) : (
          <Spinner />
        )}
      </div>

      {enrollmentReady && secretCode && (
        <div className={styles.secretKeyRow}>
          <label className={styles.secretKeyLabel}>{t('mfaSetup.manualKeyLabel')}</label>
          <code className={styles.secretKeyValue}>{formattedSecret}</code>
        </div>
      )}

      <Button
        kind="brand"
        fullWidth
        onClick={onContinue}
        disabled={!enrollmentReady}
      >
        {t('mfaSetup.submitScan')}
      </Button>
    </>
  );
}

// ─── Step 2: Verify ─────────────────────────────────────────────────────────

function Step2Verify({
  code,
  setCode,
  onComplete,
  submitting,
  errorMessage,
}: {
  code: string;
  setCode: (s: string) => void;
  onComplete: (s: string) => void;
  submitting: boolean;
  errorMessage: string | null;
}) {
  const { t } = useI18n();

  return (
    <>
      <p className={styles.body}>{t('mfaSetup.bodyVerify')}</p>

      <div className={styles.codeInputCenter}>
        <CodeInput
          value={code}
          onChange={setCode}
          onComplete={onComplete}
          ariaLabel={t('mfaSetup.headlineVerify')}
          disabled={submitting}
          errorMessage={errorMessage}
        />
      </div>

      <Button
        kind="brand"
        fullWidth
        loading={submitting}
        disabled={code.length !== 6 || submitting}
        onClick={() => onComplete(code)}
      >
        {t('mfaSetup.submitVerify')}
      </Button>
    </>
  );
}

// ─── Step 3: Save recovery codes ─────────────────────────────────────────────

function Step3Save({
  recoveryCodes,
  savedConfirmed,
  setSavedConfirmed,
}: {
  recoveryCodes: string[];
  savedConfirmed: boolean;
  setSavedConfirmed: (b: boolean) => void;
}) {
  const { t } = useI18n();

  const onDownload = () => {
    const blob = new Blob([recoveryCodes.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gc-coach-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const onCopy = () => {
    void navigator.clipboard.writeText(recoveryCodes.join('\n'));
  };

  return (
    <>
      <p className={styles.body}>{t('mfaSetup.bodySave')}</p>

      <div className={styles.recoveryCodesGrid}>
        {recoveryCodes.map((c) => (
          <span key={c} className={styles.recoveryCode}>
            {c}
          </span>
        ))}
      </div>

      <div className={styles.recoveryActions}>
        <Button kind="secondary" onClick={onDownload}>
          {t('mfaSetup.download')}
        </Button>
        <Button kind="secondary" onClick={onCopy}>
          {t('mfaSetup.copy')}
        </Button>
      </div>

      <Checkbox
        label={t('mfaSetup.confirmSaved')}
        checked={savedConfirmed}
        onChange={(e) => setSavedConfirmed(e.currentTarget.checked)}
      />

      <Button kind="brand" fullWidth disabled={!savedConfirmed}>
        {t('mfaSetup.finishButton')}
      </Button>
    </>
  );
}

// ─── Recovery code generator ────────────────────────────────────────────────

function generateRecoveryCodes(count: number): string[] {
  // 10-char alphanumeric codes, grouped 5-5 for readability.
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const out: string[] = [];
  const randomChar = () => alphabet[Math.floor(Math.random() * alphabet.length)] ?? 'A';
  for (let i = 0; i < count; i++) {
    let code = '';
    for (let j = 0; j < 10; j++) {
      if (j === 5) code += '-';
      code += randomChar();
    }
    out.push(code);
  }
  return out;
}
